import { NavLink, Outlet, Navigate, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { Shield, FileText, Users, CreditCard, LogOut, Home as HomeIcon, Type, UserSquare2, Star, Database, Mail, FileSearch, KeyRound } from "lucide-react";

const NAV = [
  { to: "/admin", label: "Overview", icon: HomeIcon, end: true },
  { to: "/admin/insights", label: "Blogs & Guidance", icon: FileText },
  { to: "/admin/content", label: "Website wording", icon: Type },
  { to: "/admin/seo", label: "SEO analysis", icon: FileSearch },
  { to: "/admin/team", label: "Team", icon: UserSquare2 },
  { to: "/admin/reviews", label: "Reviews", icon: Star },
  { to: "/admin/nbr-names", label: "NBR Register", icon: Database },
  { to: "/admin/leads", label: "Leads", icon: Users },
  { to: "/admin/customers", label: "Customers", icon: UserSquare2 },
  { to: "/admin/email-drips", label: "Drip queue", icon: Mail },
  { to: "/admin/email-templates", label: "Drip templates", icon: Mail },
  { to: "/admin/transactions", label: "Transactions", icon: CreditCard },
  { to: "/admin/security", label: "Security / 2FA", icon: KeyRound },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();

  if (loading) return <main className="py-24 text-center text-[#447694]">Loading…</main>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const handleLogout = () => {
    logout();
    nav("/admin/login");
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="lg:w-64 bg-[#04273d] text-white lg:min-h-screen lg:sticky lg:top-0 flex flex-col">
        <div className="p-5 border-b border-white/10">
          <Link to="/admin" className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
              <Shield size={16} className="text-[#71edc9]" />
            </div>
            <div className="leading-tight">
              <p className="font-display font-bold text-sm">BNP Admin</p>
              <p className="font-mono-brand text-[10px] text-[#9ac7f7] tracking-widest uppercase">{user.email}</p>
            </div>
          </Link>
        </div>

        <nav className="p-3 flex-1 flex flex-col gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                  isActive ? "bg-[#71edc9] text-[#04273d] font-semibold" : "text-[#9ac7f7] hover:bg-white/5"
                }`
              }
            >
              <n.icon size={16} />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-white/10 space-y-1">
          <Link to="/" className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm text-[#9ac7f7] hover:bg-white/5" data-testid="admin-nav-back-to-site">
            <HomeIcon size={16} /> Back to site
          </Link>
          <button onClick={handleLogout} className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm text-[#9ac7f7] hover:bg-white/5" data-testid="admin-nav-logout-button">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 bg-[#f7f7f7] min-h-screen">
        <Outlet />
      </main>
    </div>
  );
}
